import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../helpers/api';
import { deleteRecommendationArticle, getFilteredRecommendationArticles } from '../helpers/recommendation';

const RecommendationArticlesList = () => {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10
  });

  useEffect(() => {
    fetchArticles();
  }, [pagination.currentPage, searchTerm]);

  const fetchArticles = async () => {
    try {
      setIsLoading(true);
      
      // Create filters object based on your current state
      const filters = {
        search: searchTerm,
        sort: 'id:desc'
      };

      // Call getFilteredRecommendationArticles with current pagination
      const { data, pagination: paginationData } = await getFilteredRecommendationArticles(
        filters,
        pagination.currentPage,
        pagination.pageSize
      );

      setArticles(data);
      setPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(paginationData.total / pagination.pageSize)
      }));
    } catch (err) {
      setError('Erreur lors du chargement des articles de recommandation');
      console.error('Error fetching recommendation articles:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article de recommandation ?')) {
      return;
    }

    try {
      const response = await deleteRecommendationArticle(id);
      if(response){
        setArticles(prev => prev.filter(article => article.documentId !== id));
        // Afficher message de succès
        const successMessage = document.getElementById('successMessage');
        successMessage.innerText = 'Article supprimé avec succès';
        successMessage.style.display = 'block';
        setTimeout(() => {
          successMessage.style.display = 'none';
        }, 3000);
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error('Error deleting recommendation article:', err);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  if (isLoading && articles.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Gestion des articles de recommandation</h2>
        <Link to="/recommendation-articles/new" className="btn btn-primary">
          <i className="bi bi-plus-lg me-2"></i>
          Ajouter un article
        </Link>
      </div>

      <div className="alert alert-success" id="successMessage" style={{ display: 'none' }}></div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-8">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Rechercher un article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="bg-light">
              <tr>
                <th>Titre</th>
                <th>Durée</th>
                <th>Difficulté</th>
                <th>Ordre d'affichage</th>
                <th>Lieux</th>
                <th width="150">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map(article => (
                <tr key={article.id}>
                  <td className="align-middle">
                    <div className="d-flex align-items-center">
                      <img 
                        src={API_BASE_URL + (article.coverImage?.formats?.thumbnail?.url || article.coverImage?.url || '/placeholder.jpg')} 
                        alt="" 
                        className="rounded me-2"
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                      />
                      <div>
                        <div className="fw-semibold">{article.title}</div>
                        <small className="text-muted">{article.description ? article.description.substring(0, 50) + (article.description.length > 50 ? '...' : '') : ''}</small>
                      </div>
                    </div>
                  </td>
                  <td className="align-middle">{article.duration || '-'}</td>
                  <td className="align-middle">{article.difficulty || '-'}</td>
                  <td className="align-middle">{article.featureOrder || '-'}</td>
                  <td className="align-middle">{article.locations?.length || 0} lieux</td>
                  <td className="align-middle">
                    <div className="btn-group">
                      <Link 
                        to={`/recommendation-articles/${article.documentId}`}
                        className="btn btn-sm btn-outline-primary"
                        title="Éditer"
                      >
                        <i className="bi bi-pencil"></i>
                      </Link>
                      <button
                        onClick={() => handleDelete(article.documentId)}
                        className="btn btn-sm btn-outline-danger"
                        title="Supprimer"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              
              {articles.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    Aucun article de recommandation trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
            </li>
            
            {[...Array(pagination.totalPages)].map((_, index) => (
              <li 
                key={index + 1}
                className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}

            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default RecommendationArticlesList;